'use client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import * as z from 'zod';

import { LockIcon as IconLock } from 'lucide-react';

import TextInput from '@/components/ui/TextInput/TextInput';
import { useNookContext } from '@/components/providers/NookContextProvider';
import useUnlockNookMutation from '@/hooks/mutations/useUnlockNookMutation';
import { parseError } from '@/lib/errorHandlers';
import { passwordSchema } from '@/lib/schemas';
import showToast from '@/lib/showToast';

const unlockFormSchema = z.object({
  password: passwordSchema,
});

type UnlockFormSchema = z.infer<typeof unlockFormSchema>;

const UnlockNookForm = () => {
  const { id } = useNookContext();
  const router = useRouter();
  const form = useForm<UnlockFormSchema>({
    resolver: zodResolver(unlockFormSchema),
  });

  const unlockNookMutation = useUnlockNookMutation();

  const onSubmit = async (data: UnlockFormSchema) => {
    try {
      if (!id) {
        throw new Error('Nook ID is missing');
      }
      const response = await unlockNookMutation.mutateAsync({
        nookId: id,
        password: data.password,
      });
      showToast(response.message, 'success');
      router.refresh();
    } catch (error) {
      const err = parseError(error);
      form.setError('password', {
        message: err.message,
      });
    }
  };

  return (
    <form
      className='flex flex-col gap-2'
      onSubmit={form.handleSubmit(onSubmit)}
    >
      <TextInput<UnlockFormSchema>
        type='password'
        control={form.control}
        name='password'
        placeholder='********'
        icon={<IconLock size={18} />}
        aria-label='Password to unlock nook'
      />
      <button type='submit' className='btn btn-primary w-full'>
        Submit
      </button>
    </form>
  );
};

export default UnlockNookForm;
